import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { UserRead } from "@/types";
import { getMe, logout as apiLogout } from "@/api/auth";
import { getToken } from "@/api/client";

const LS_LAST_USER = "comfyviz_last_user_id";

/** Clear all comfyviz_* keys from localStorage & sessionStorage (except user marker & lang). */
function clearAppCache(): void {
  const keep = new Set([LS_LAST_USER, "comfyviz_lang"]);
  const lsKeys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith("comfyviz_") && !keep.has(k)) lsKeys.push(k);
  }
  lsKeys.forEach((k) => localStorage.removeItem(k));
  const ssKeys: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k && k.startsWith("comfyviz_")) ssKeys.push(k);
  }
  ssKeys.forEach((k) => sessionStorage.removeItem(k));
}

/**
 * Called on Login page mount — ensures no stale data from a previous session.
 * Safe to call multiple times (idempotent).
 */
export function clearAppCacheOnLogin(): void {
  clearAppCache();
}

/** If the current user differs from the last one, wipe cached data. */
function ensureUserCache(user: UserRead): void {
  const prev = localStorage.getItem(LS_LAST_USER);
  if (prev !== user.id) {
    clearAppCache();
    localStorage.setItem(LS_LAST_USER, user.id);
  }
}

interface AuthCtx {
  user: UserRead | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => void;
}

const Ctx = createContext<AuthCtx>({
  user: null,
  loading: true,
  refresh: async () => {},
  logout: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserRead | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await getMe();
      ensureUserCache(me);
      setUser(me);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => {
    apiLogout();
    setUser(null);
  };

  return (
    <Ctx.Provider value={{ user, loading, refresh, logout }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  return useContext(Ctx);
}
